import React, { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Spin } from "antd";

import { validateInvitation } from "../../api/marketOverviewApi";
import ResetPasswordPage from "./ResetPasswordPage";
import InvitationExpiredPage from "./InvitationExpiredPage";
import InvitationInvalidPage from "./InvitationInvalidPage";
import logger from "../../utils/logger";

/**
 * Thin orchestrator for the auth email landing flow.
 *
 * Email links now point here directly instead of round-tripping through the
 * API's `GET /users/verify` 307 redirect. This page validates the token via
 * `GET /users/validate-invitation` and renders the matching state client-side.
 *
 * - `mode="invite"` → `/accept-invite` (new + resend invitation links)
 * - `mode="reset"`  → `/reset-password` (password-reset links; this adds
 *   server-side pre-validation on top of the existing reset form)
 *
 * Valid tokens render the existing `ResetPasswordPage` (which reads token +
 * email from the same query string), so there is no page duplication.
 */
const AcceptInvitePage = ({ mode = "invite" }) => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  // loading | valid | expired | already_activated | invalid | error
  const [state, setState] = useState("loading");

  useEffect(() => {
    let cancelled = false;

    if (!token || !email) {
      setState("invalid");
      return;
    }

    setState("loading");
    validateInvitation({ token, email, mode })
      .then((data) => {
        if (cancelled) return;
        const status = data?.status;
        if (
          ["valid", "expired", "already_activated", "invalid"].includes(status)
        ) {
          setState(status);
        } else {
          setState("invalid");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        // Transient (503/429): offer a retry rather than a terminal dead-link.
        if (err?.transient) {
          setState("error");
        } else {
          logger.error("Invitation validation failed:", err);
          setState("invalid");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, email, mode]);

  if (state === "loading") {
    return (
      <div className="flex justify-center items-center h-[100vh] w-[100vw] bg-white">
        <Spin size="large" />
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="flex flex-col justify-center items-center h-[100vh] w-[100vw] bg-white gap-4 px-4 text-center">
        <div className="text-[18px] font-semibold text-[#101828]">
          Something went wrong
        </div>
        <div className="text-[14px] text-[#475467] max-w-[31.625rem]">
          We couldn't verify your link right now. Please refresh the page or try
          again in a few minutes.
        </div>
      </div>
    );
  }

  if (state === "valid") return <ResetPasswordPage />;
  if (state === "expired") return <InvitationExpiredPage mode={mode} />;
  if (state === "already_activated")
    return <Navigate to="/login?info=already_activated" replace />;

  // invalid (or any unexpected status / missing params)
  return <InvitationInvalidPage mode={mode} />;
};

export default AcceptInvitePage;
